import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Target, Lightbulb, BarChart3, ShieldAlert, Plus } from "lucide-react";
import { toast } from "sonner";

const SIGNAL = { porta_attivita: { t: "Porta un'attività", c: "text-[#FFD700]" }, porta_amico: { t: "Porta un amico", c: "text-[#00E676]" }, equilibrio: { t: "Equilibrio", c: "text-zinc-400" } };

export default function GrowthAdvancedCeo() {
  const [opp, setOpp] = useState(null);
  const [an, setAn] = useState(null);
  const [missions, setMissions] = useState([]);
  const [fraud, setFraud] = useState(null);
  const [mf, setMf] = useState({ title: "", type: "person", comune: "", category: "", target: 1, reward_points: 500, active: true });

  const load = () => {
    api.get("/ceo/growth/opportunities").then(({ data }) => setOpp(data)).catch(() => {});
    api.get("/ceo/growth/analytics").then(({ data }) => setAn(data)).catch(() => {});
    api.get("/ceo/growth/missions").then(({ data }) => setMissions(data.missions)).catch(() => {});
    api.get("/ceo/growth/fraud/audit").then(({ data }) => setFraud(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const eur = (v) => "€" + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const createMission = async () => {
    try { await api.post("/ceo/growth/missions", mf); toast.success("Missione creata"); setMf({ ...mf, title: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delMission = async (id) => { await api.delete(`/ceo/growth/missions/${id}`); load(); };

  const roi = an?.roi || {}; const ue = an?.unit_economics || {};
  return (
    <div className="space-y-5 mt-6 pt-6 border-t border-white/10" data-testid="growth-advanced">
      {/* Analytics + ROI */}
      <div>
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#00E676]" /> Analytics & ROI Referral</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">GMV generato</div><div className="text-xl font-black">{eur(roi.gmv_generated)}</div></div>
          <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Costo reward</div><div className="text-xl font-black">{eur(roi.reward_cost_eur)}</div></div>
          <div className={`rounded-2xl border p-4 ${roi.self_financing ? "bg-gradient-to-br from-[#00E676]/15 to-transparent border-[#00E676]/30" : "bg-[#1C1C1F] border-white/10"}`} data-testid="growth-roi"><div className="text-[10px] uppercase tracking-widest text-zinc-500">ROI</div><div className="text-xl font-black">{roi.roi_ratio != null ? `${roi.roi_ratio}x` : "—"}</div></div>
          <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">CAC referral</div><div className="text-xl font-black">{eur(ue.cac_eur)}</div></div>
        </div>
        {an && <p className="text-xs text-zinc-500 mt-2">Clienti: {an.customers.invited} invitati · {an.customers.conversion_rate}% al 1° ordine · {eur(an.customers.gmv)} GMV. Attività: {an.businesses.referred} portate · {an.businesses.active} attive · {eur(an.businesses.gmv)} GMV.</p>}
      </div>

      {/* Growth Opportunities */}
      <div>
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Lightbulb className="h-4 w-4 text-[#FFD700]" /> Growth Opportunities (per comune)</div>
        <div className="space-y-2">
          {(opp?.opportunities || []).map((o) => (
            <div key={o.comune} data-testid={`opp-${o.comune}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-semibold">{o.comune} <span className="text-xs text-zinc-500">· Growth Score {o.growth_score}</span></div>
                <span className={`text-xs font-semibold ${SIGNAL[o.signal]?.c}`}>→ {SIGNAL[o.signal]?.t}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{o.active_users} utenti attivi · {o.active_businesses}/{o.businesses} attività attive · {eur(o.gmv)} GMV{o.categories_missing?.length ? ` · Mancano: ${o.categories_missing.slice(0, 4).join(", ")}` : ""}{o.top_requested?.length ? ` · Richiesta: ${o.top_requested[0].name} (${o.top_requested[0].requests})` : ""}</div>
            </div>
          ))}
          {opp && opp.opportunities.length === 0 && <div className="text-zinc-500 text-sm">Nessun dato comune.</div>}
        </div>
      </div>

      {/* Growth Missions */}
      <div>
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-[#00E676]" /> Missioni di crescita territoriali</div>
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
          <div className="sm:col-span-2"><Input data-testid="gm-title" placeholder="Titolo" value={mf.title} onChange={(e) => setMf({ ...mf, title: e.target.value })} className="bg-[#0A0A0C] border-white/10 h-9" /></div>
          <select value={mf.type} onChange={(e) => setMf({ ...mf, type: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-2 text-sm"><option value="person">Persona</option><option value="business">Attività</option></select>
          <Input placeholder="Comune" value={mf.comune} onChange={(e) => setMf({ ...mf, comune: e.target.value })} className="bg-[#0A0A0C] border-white/10 h-9" />
          <Input type="number" placeholder="Target" value={mf.target} onChange={(e) => setMf({ ...mf, target: parseInt(e.target.value) || 1 })} className="bg-[#0A0A0C] border-white/10 h-9" />
          <Input type="number" placeholder="Punti" value={mf.reward_points} onChange={(e) => setMf({ ...mf, reward_points: parseInt(e.target.value) || 0 })} className="bg-[#0A0A0C] border-white/10 h-9" />
        </div>
        <Button data-testid="gm-create" onClick={createMission} className="bg-[#00E676] text-black rounded-md mt-2 h-8 text-xs"><Plus className="h-4 w-4 mr-1" /> Crea missione</Button>
        <div className="space-y-1.5 mt-3">
          {missions.map((m) => (
            <div key={m.id} className="flex items-center justify-between text-sm border-t border-white/5 pt-1.5">
              <span className="text-zinc-400">{m.title} · {m.type}{m.comune ? ` · ${m.comune}` : ""} · target {m.target} · {m.reward_points} pt {m.active ? "" : "(off)"}</span>
              <button onClick={() => delMission(m.id)} className="text-red-400 text-xs">Elimina</button>
            </div>
          ))}
        </div>
      </div>

      {/* Fraud */}
      <div>
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-red-400" /> Anti-frode & audit log ({fraud?.blocked_users || 0} bloccati)</div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {(fraud?.audit || []).map((a, i) => (
            <div key={i} className="text-xs text-zinc-500 border-t border-white/5 pt-1">{(a.at || "").slice(0, 16)} · {a.action} · {a.target}{a.points ? ` (${a.points} pt)` : ""}</div>
          ))}
          {fraud && fraud.audit.length === 0 && <div className="text-zinc-600 text-xs">Nessuna azione registrata.</div>}
        </div>
      </div>
    </div>
  );
}
